// services/paymentProcessingService.js
import { supabase } from '../data/supabase';

export const paymentProcessingService = {
  /**
   * Processa a compra de um pacote de sessões
   * @param {Object} orderData - Dados do pedido do Stripe
   * @param {string} patientId - ID do paciente
   */
  async processPackagePurchase(orderData, patientId) {
    console.log('Processando compra de pacote para paciente:', patientId);
    
    if (!orderData || !orderData.session_details || !patientId) {
      throw new Error('Dados insuficientes para processar compra de pacote');
    }
    
    // Extrair metadados da sessão
    const metadata = orderData.session_details.metadata || {};
    const productType = metadata.productType || 'individual';
    
    // Extrair informações do pacote
    let sessions = 4; // Valor padrão
    let validity = 30; // Valor padrão em dias
    let sessionValidity = 7; // Valor padrão em dias
    
    // Tentar extrair valores dos metadados
    if (metadata.sessions) {
      sessions = parseInt(metadata.sessions, 10);
    }
    
    if (metadata.validity) {
      validity = parseInt(metadata.validity, 10);
    }
    
    if (metadata.sessionValidity) {
      sessionValidity = parseInt(metadata.sessionValidity, 10);
    }
    
    // Calcular data de expiração do pacote
    const packageExpiry = new Date();
    packageExpiry.setDate(packageExpiry.getDate() + validity);
    
    // Calcular data de expiração da sessão
    const sessionExpiry = new Date();
    sessionExpiry.setDate(sessionExpiry.getDate() + sessionValidity);
    
    try {
      // 1. Inserir o pacote na tabela packages
      const { data: packageData, error: packageError } = await supabase
        .from('packages')
        .insert({
          patient_id: patientId,
          type: productType,
          total_sessions: sessions,
          remaining_sessions: sessions,
          package_expiry: packageExpiry.toISOString(),
          created_at: new Date().toISOString(),
          stripe_price_id: orderData.session_details.line_items?.data[0]?.price?.id || null,
          stripe_product_id: orderData.product_details?.id || null,
          status: 'active',
          session_validity_days: sessionValidity
        })
        .select()
        .single();
      
      if (packageError) {
        console.error('Erro ao inserir pacote:', packageError);
        throw new Error('Falha ao registrar pacote');
      }
      
      console.log('Pacote criado com sucesso:', packageData);
      
      // 2. Obter ou criar a carteira do paciente com o saldo inicial correto
      const walletData = await this.getOrCreateWallet(patientId, sessions, 'package', productType);
      
      // 3. Registrar a transação na tabela credit_transactions
      const { error: transactionError } = await supabase
        .from('credit_transactions')
        .insert({
          wallet_id: walletData.id,
          amount: sessions,
          transaction_type: 'purchase',
          source_type: 'package',
          source_id: packageData.id,
          created_at: new Date().toISOString(),
          notes: `Compra de pacote ${productType} com ${sessions} sessões`
        });
      
      if (transactionError) {
        console.error('Erro ao registrar transação:', transactionError);
        throw new Error('Falha ao registrar transação');
      }
      
      console.log('Transação registrada com sucesso para o pacote');
      
      // 4. Criar as sessões individuais do pacote na tabela sessions
      const sessionPromises = [];
      
      for (let i = 0; i < sessions; i++) {
        const sessionPromise = supabase
          .from('sessions')
          .insert({
            package_id: packageData.id,
            session_expiry: sessionExpiry.toISOString(),
            status: 'available',
            created_at: new Date().toISOString()
          });
        
        sessionPromises.push(sessionPromise);
      }
      
      // Aguardar todas as inserções de sessões
      const sessionResults = await Promise.all(sessionPromises);
      
      // Verificar se houve erros nas inserções de sessões
      const sessionErrors = sessionResults.filter(result => result.error);
      
      if (sessionErrors.length > 0) {
        console.error('Erros ao inserir sessões:', sessionErrors);
        throw new Error('Falha ao registrar algumas sessões do pacote');
      }
      
      console.log(`${sessions} sessões criadas com sucesso para o pacote ${packageData.id}`);
      
      return {
        success: true,
        packageId: packageData.id,
        sessions: sessions
      };
    } catch (error) {
      console.error('Erro ao processar compra de pacote:', error);
      throw error;
    }
  },
  
  /**
   * Processa a compra de uma sessão individual
   * @param {Object} orderData - Dados do pedido do Stripe
   * @param {string} patientId - ID do paciente
   */
  async processSingleSessionPurchase(orderData, patientId) {
    console.log('Processando compra de crédito individual para paciente:', patientId);
    
    if (!orderData || !orderData.session_details || !patientId) {
      throw new Error('Dados insuficientes para processar compra de crédito');
    }
    
    // Extrair metadados da sessão
    const metadata = orderData.session_details.metadata || {};
    const creditType = metadata.productType || 'individual';
    
    // Definir validade da sessão (7 dias por padrão)
    const sessionValidity = metadata.sessionValidity ? parseInt(metadata.sessionValidity, 10) : 7;
    
    // Calcular data de expiração da sessão
    const sessionExpiry = new Date();
    sessionExpiry.setDate(sessionExpiry.getDate() + sessionValidity);
    
    try {
      // 1. Inserir o crédito na tabela individual_credits
      const { data: creditData, error: creditError } = await supabase
        .from('individual_credits')
        .insert({
          patient_id: patientId,
          type: creditType,
          status: 'available',
          created_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (creditError) {
        console.error('Erro ao inserir crédito individual:', creditError);
        throw new Error('Falha ao registrar crédito individual');
      }
      
      console.log('Crédito individual criado com sucesso:', creditData);
      
      // 2. Obter ou criar a carteira do paciente com o saldo inicial correto
      const walletData = await this.getOrCreateWallet(patientId, 1, 'individual', creditType);
      
      // 3. Registrar a transação na tabela credit_transactions
      const { error: transactionError } = await supabase
        .from('credit_transactions')
        .insert({
          wallet_id: walletData.id,
          amount: 1, // Um crédito individual
          transaction_type: 'purchase',
          source_type: 'individual_credit',
          source_id: creditData.id,
          created_at: new Date().toISOString(),
          notes: `Compra de crédito individual do tipo ${creditType}`
        });
      
      if (transactionError) {
        console.error('Erro ao registrar transação:', transactionError);
        throw new Error('Falha ao registrar transação');
      }
      
      console.log('Transação registrada com sucesso para o crédito individual');
      
      // 4. Criar uma sessão na tabela sessions
      const { error: sessionError } = await supabase
        .from('sessions')
        .insert({
          individual_credit_id: creditData.id,
          session_expiry: sessionExpiry.toISOString(),
          status: 'available',
          created_at: new Date().toISOString()
        });
      
      if (sessionError) {
        console.error('Erro ao criar sessão para crédito individual:', sessionError);
        throw new Error('Falha ao criar sessão para crédito individual');
      }
      
      console.log('Sessão criada com sucesso para o crédito individual');
      
      return {
        success: true,
        creditId: creditData.id
      };
    } catch (error) {
      console.error('Erro ao processar compra de crédito individual:', error);
      throw error;
    }
  },
  
  /**
   * Processa o cancelamento de um agendamento e devolve o crédito
   * @param {Object} appointmentData - Dados do agendamento
   */
  async processAppointmentCancellation(appointmentData) {
    console.log('Processando cancelamento de agendamento:', appointmentData.id);
    
    if (!appointmentData) {
      throw new Error('Dados insuficientes para processar cancelamento');
    }
    
    // Verificar se é um agendamento de pacote ou crédito individual
    if (appointmentData.is_package && appointmentData.package_session_id) {
      // Cancelamento de sessão de pacote
      return this.processPackageSessionCancellation(appointmentData);
    } else if (appointmentData.individual_credit_id) {
      // Cancelamento de crédito individual
      return this.processIndividualCreditCancellation(appointmentData);
    } else {
      throw new Error('Tipo de agendamento não identificado');
    }
  },
  
  /**
   * Processa o cancelamento de uma sessão de pacote
   * @param {Object} appointmentData - Dados do agendamento
   */
  async processPackageSessionCancellation(appointmentData) {
    try {
      // 1. Atualizar o status da sessão para disponível
      const { error: sessionError } = await supabase
        .from('sessions')
        .update({
          appointment_id: null,
          status: 'available'
        })
        .eq('id', appointmentData.package_session_id);
      
      if (sessionError) {
        console.error('Erro ao atualizar sessão:', sessionError);
        throw new Error('Falha ao atualizar status da sessão');
      }
      
      // 2. Incrementar o contador de sessões restantes no pacote
      const { data: packageData, error: packageError } = await supabase
        .from('packages')
        .select('remaining_sessions, type')
        .eq('id', appointmentData.package_id)
        .single();
      
      if (packageError) {
        console.error('Erro ao buscar pacote:', packageError);
        throw new Error('Falha ao buscar informações do pacote');
      }
      
      const { error: updatePackageError } = await supabase
        .from('packages')
        .update({
          remaining_sessions: packageData.remaining_sessions + 1,
          status: 'active' // Garantir que o pacote esteja ativo
        })
        .eq('id', appointmentData.package_id);
      
      if (updatePackageError) {
        console.error('Erro ao atualizar pacote:', updatePackageError);
        throw new Error('Falha ao atualizar contador de sessões do pacote');
      }
      
      // 3. Buscar a carteira do paciente
      const { data: wallets, error: walletError } = await supabase
        .from('wallet')
        .select('*')
        .eq('patient_id', appointmentData.patient_id);
      
      if (walletError) {
        console.error('Erro ao buscar carteira:', walletError);
        throw new Error('Falha ao buscar carteira do paciente');
      }
      
      // Encontrar a carteira correta para este tipo de pacote
      let walletToUpdate = wallets.find(w => w.type === 'package' && w.session_type === packageData.type);
      
      // Se não encontrar uma carteira específica, usar a primeira carteira de pacote
      if (!walletToUpdate) {
        walletToUpdate = wallets.find(w => w.type === 'package');
      }
      
      // Se ainda não encontrar, usar qualquer carteira
      if (!walletToUpdate && wallets.length > 0) {
        walletToUpdate = wallets[0];
      }
      
      if (!walletToUpdate) {
        console.error('Nenhuma carteira encontrada para o paciente:', appointmentData.patient_id);
        throw new Error('Carteira do paciente não encontrada');
      }
      
      // 4. Atualizar o saldo da carteira
      const { error: updateWalletError } = await supabase
        .from('wallet')
        .update({
          balance: walletToUpdate.balance + 1,
          updated_at: new Date().toISOString()
        })
        .eq('id', walletToUpdate.id);
      
      if (updateWalletError) {
        console.error('Erro ao atualizar saldo da carteira:', updateWalletError);
        throw new Error('Falha ao atualizar saldo da carteira');
      }
      
      // 5. Registrar a transação de devolução
      const { error: transactionError } = await supabase
        .from('credit_transactions')
        .insert({
          wallet_id: walletToUpdate.id,
          amount: 1,
          transaction_type: 'refund',
          source_type: 'package',
          source_id: appointmentData.package_id,
          appointment_id: appointmentData.id,
          created_at: new Date().toISOString(),
          notes: `Cancelamento de agendamento com devolução de sessão de pacote ${packageData.type}`
        });
      
      if (transactionError) {
        console.error('Erro ao registrar transação de devolução:', transactionError);
        throw new Error('Falha ao registrar transação de devolução');
      }
      
      return {
        success: true,
        message: 'Sessão de pacote devolvida com sucesso'
      };
    } catch (error) {
      console.error('Erro ao processar cancelamento de sessão de pacote:', error);
      throw error;
    }
  },
  
  /**
   * Processa o cancelamento de um crédito individual
   * @param {Object} appointmentData - Dados do agendamento
   */
  async processIndividualCreditCancellation(appointmentData) {
    try {
      // 1. Atualizar o status do crédito para disponível
      const { data: creditData, error: creditError } = await supabase
        .from('individual_credits')
        .update({
          appointment_id: null,
          status: 'available',
          used_at: null
        })
        .eq('id', appointmentData.individual_credit_id)
        .select('type')
        .single();
      
      if (creditError) {
        console.error('Erro ao atualizar crédito:', creditError);
        throw new Error('Falha ao atualizar status do crédito');
      }
      
      // 2. Atualizar o status da sessão para disponível
      const { error: sessionError } = await supabase
        .from('sessions')
        .update({
          appointment_id: null,
          status: 'available'
        })
        .eq('individual_credit_id', appointmentData.individual_credit_id);
      
      if (sessionError) {
        console.error('Erro ao atualizar sessão:', sessionError);
        throw new Error('Falha ao atualizar status da sessão');
      }
      
      // 3. Buscar a carteira do paciente
      const { data: wallets, error: walletError } = await supabase
        .from('wallet')
        .select('*')
        .eq('patient_id', appointmentData.patient_id);
      
      if (walletError) {
        console.error('Erro ao buscar carteira:', walletError);
        throw new Error('Falha ao buscar carteira do paciente');
      }
      
      // Encontrar a carteira correta para este tipo de crédito
      let walletToUpdate = wallets.find(w => w.type === 'individual' && w.session_type === creditData.type);
      
      // Se não encontrar uma carteira específica, usar a primeira carteira individual
      if (!walletToUpdate) {
        walletToUpdate = wallets.find(w => w.type === 'individual');
      }
      
      // Se ainda não encontrar, usar qualquer carteira
      if (!walletToUpdate && wallets.length > 0) {
        walletToUpdate = wallets[0];
      }
      
      if (!walletToUpdate) {
        console.error('Nenhuma carteira encontrada para o paciente:', appointmentData.patient_id);
        throw new Error('Carteira do paciente não encontrada');
      }
      
      // 4. Atualizar o saldo da carteira
      const { error: updateWalletError } = await supabase
        .from('wallet')
        .update({
          balance: walletToUpdate.balance + 1,
          updated_at: new Date().toISOString()
        })
        .eq('id', walletToUpdate.id);
      
      if (updateWalletError) {
        console.error('Erro ao atualizar saldo da carteira:', updateWalletError);
        throw new Error('Falha ao atualizar saldo da carteira');
      }
      
      // 5. Registrar a transação de devolução
      const { error: transactionError } = await supabase
        .from('credit_transactions')
        .insert({
          wallet_id: walletToUpdate.id,
          amount: 1,
          transaction_type: 'refund',
          source_type: 'individual_credit',
          source_id: appointmentData.individual_credit_id,
          appointment_id: appointmentData.id,
          created_at: new Date().toISOString(),
          notes: `Cancelamento de agendamento com devolução de crédito ${creditData.type}`
        });
      
      if (transactionError) {
        console.error('Erro ao registrar transação de devolução:', transactionError);
        throw new Error('Falha ao registrar transação de devolução');
      }
      
      return {
        success: true,
        message: 'Crédito individual devolvido com sucesso'
      };
    } catch (error) {
      console.error('Erro ao processar cancelamento de crédito individual:', error);
      throw error;
    }
  },

  /**
   * Obtém ou cria uma carteira para o paciente
   * @param {string} patientId - ID do paciente
   * @param {number} initialBalance - Saldo inicial
   * @param {string} type - Tipo da carteira ('individual' ou 'package')
   * @param {string} sessionType - Tipo de sessão ('individual', 'casal', 'familia')
   * @returns {Object} - Dados da carteira
   */
  async getOrCreateWallet(patientId, initialBalance = 0, type = 'individual', sessionType = 'individual') {
    if (!patientId) {
      throw new Error('ID do paciente é obrigatório');
    }
    
    console.log('Buscando carteira para paciente:', patientId);
    console.log('Parâmetros:', { initialBalance, type, sessionType });
    
    try {
      // Buscar carteira existente do mesmo tipo e tipo de sessão
      const { data: existingWallets, error: walletError } = await supabase
        .from('wallet')
        .select('*')
        .eq('patient_id', patientId);
      
      if (walletError) {
        console.error('Erro ao buscar carteiras:', walletError);
        throw new Error(`Erro ao buscar carteiras: ${walletError.message}`);
      }
      
      // Procurar uma carteira que corresponda ao tipo e tipo de sessão
      let wallet = existingWallets.find(w => 
        w.type === type && w.session_type === sessionType
      );
      
      // Se encontrou uma carteira correspondente, atualizar o saldo
      if (wallet) {
        console.log('Carteira encontrada. Atualizando saldo:', wallet);
        
        const newBalance = wallet.balance + initialBalance;
        const { data: updatedWallet, error: updateError } = await supabase
          .from('wallet')
          .update({
            balance: newBalance,
            updated_at: new Date().toISOString()
          })
          .eq('id', wallet.id)
          .select()
          .single();
        
        if (updateError) {
          console.error('Erro ao atualizar carteira:', updateError);
          throw new Error(`Falha ao atualizar carteira: ${updateError.message}`);
        }
        
        console.log('Carteira atualizada com sucesso:', updatedWallet);
        return updatedWallet;
      }
      
      // Se não encontrou uma carteira correspondente, criar uma nova
      console.log('Carteira não encontrada. Criando nova carteira.');
      
      const { data: newWallet, error: createError } = await supabase
        .from('wallet')
        .insert({
          patient_id: patientId,
          balance: initialBalance,
          type: type,
          session_type: sessionType,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (createError) {
        console.error('Erro ao criar carteira:', createError);
        throw new Error(`Falha ao criar carteira: ${createError.message}`);
      }
      
      console.log('Nova carteira criada com sucesso:', newWallet);
      return newWallet;
    } catch (error) {
      console.error('Erro em getOrCreateWallet:', error);
      throw error;
    }
  }
};